import React from "react";
import { useSeo } from "@/lib/useSeo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { Button, Eyebrow } from "@/components/ui-kit";
import { CTASection } from "@/components/CTASection";
import { IMG, PROCESS } from "@/data/content";
import { SERVICES, COMMERCIAL } from "@/data/site";
import { SEO } from "@/data/seo";

function ServiceBlock({ s, index }) {
  const flip = index % 2 === 1;
  return (
    <section id={s.id} className={`scroll-mt-28 py-20 lg:py-28 ${flip ? "bg-paper-warm" : "bg-paper"}`} data-testid={`service-${s.id}`}>
      <div className="mx-auto grid max-w-8xl gap-12 px-6 lg:grid-cols-12 lg:gap-20 lg:px-10">
        <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
          <span className="font-serif text-4xl text-teal" aria-hidden="true">{s.n}</span>
          <h2 className="mt-5 text-3xl font-normal leading-[1.15] tracking-tightish text-charcoal sm:text-4xl lg:text-5xl">{s.title}</h2>
          <p className="mt-6 text-lg leading-[1.7] text-charcoal/80">{s.summary}</p>
          <p className="mt-5 text-base leading-[1.8] text-charcoal/65">{s.detail}</p>
        </Reveal>
        <Reveal delay={0.1} className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
          <div className="border border-charcoal/10 p-8 lg:p-10">
            <p className="text-xs uppercase tracking-mega text-teal-deep">What it includes</p>
            <ul className="mt-6 space-y-4">
              {s.bullets.map((b) => (
                <li key={b} className="flex gap-4 text-base leading-[1.7] text-charcoal/80">
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-teal" aria-hidden="true" />
                  {b}
                </li>
              ))}
            </ul>
            <p className="mt-8 border-t border-charcoal/10 pt-6 text-sm leading-[1.8] text-charcoal/60">
              <span className="text-charcoal">Best fit — </span>{s.fit}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  useSeo({ ...SEO["/services"], image: IMG.meetingTeam, path: "/services" });

  return (
    <>
      <PageHero
        eyebrow="Services"
        titleLines={["Senior delivery,", "structured three ways."]}
        subtitle="Assessment and Roadmap, Fractional Program Leadership, and PMN-Led Delivery Teams — scoped to the initiative, accountable to the sponsor."
        image={IMG.meetingTeam}
      />

      {/* Overview strip */}
      <section className="border-b border-charcoal/10 bg-paper py-14">
        <div className="mx-auto grid max-w-8xl gap-8 px-6 md:grid-cols-3 lg:px-10">
          {SERVICES.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="group flex items-start gap-4" data-testid={`services-jump-${s.id}`}>
              <span className="font-serif text-2xl text-teal" aria-hidden="true">{s.n}</span>
              <span>
                <span className="block text-lg tracking-tightish text-charcoal transition-colors group-hover:text-teal">{s.title}</span>
                <span className="mt-1 block text-sm leading-[1.7] text-charcoal/70">{s.fit}</span>
              </span>
            </a>
          ))}
        </div>
      </section>

      {SERVICES.map((s, i) => <ServiceBlock key={s.id} s={s} index={i} />)}

      {/* Commercial structure */}
      <section className="bg-charcoal py-24 text-paper lg:py-32" data-testid="services-commercial">
        <div className="mx-auto grid max-w-8xl gap-10 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-6">
            <Eyebrow dark>{COMMERCIAL.eyebrow}</Eyebrow>
            <h2 className="mt-6 max-w-xl text-3xl font-normal leading-[1.15] tracking-tightish sm:text-4xl lg:text-5xl">{COMMERCIAL.title}</h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:pt-14">
            <p className="text-base leading-[1.8] text-paper/75">{COMMERCIAL.body}</p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {["Fixed fee", "Monthly retainer", "Milestone-based", "Time and materials"].map((m) => (
                <li key={m} className="border border-paper/15 px-5 py-3 text-sm tracking-wideish text-paper/85">{m}</li>
              ))}
            </ul>
            <Button to="/contact" variant="light" className="mt-10" track="primary" data-testid="services-contact-btn">Discuss an Initiative</Button>
          </Reveal>
        </div>
      </section>

      {/* How an engagement runs */}
      <section className="bg-paper py-24 lg:py-32">
        <div className="mx-auto max-w-8xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>How an engagement runs</Eyebrow>
            <h2 className="mt-6 max-w-2xl text-3xl font-normal leading-[1.15] tracking-tightish text-charcoal sm:text-4xl lg:text-5xl">
              Five phases. One accountable leader.
            </h2>
          </Reveal>
          <ol className="mt-16 grid gap-px overflow-hidden border border-charcoal/10 bg-charcoal/10 md:grid-cols-2 lg:grid-cols-5">
            {PROCESS.map((p, i) => (
              <Reveal as="li" key={p.n} delay={i * 0.06} className="h-full bg-paper p-8">
                <span className="font-serif text-2xl text-teal" aria-hidden="true">{p.n}</span>
                <h3 className="mt-4 text-lg font-medium tracking-tightish text-charcoal">{p.title}</h3>
                <p className="mt-3 text-sm leading-[1.8] text-charcoal/65">{p.body}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <CTASection
        eyebrow="Not sure which structure fits?"
        title="Start with the initiative. We'll recommend the structure."
        body="Most conversations begin with a specific program or decision. Tell us about it and we'll suggest whether an assessment, embedded leadership, or a PMN-led team makes sense."
        image={IMG.boardroomWarm}
      />
    </>
  );
}
